import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Truck,
  LayoutDashboard,
  ClipboardList,
  Navigation,
  Navigation2,
  DollarSign,
  ClipboardCheck,
  User,
  RefreshCw,
  PhoneCall,
  ChevronDown,
  ArrowLeft,
  AlertCircle,
  Menu,
  X,
  Bell,
  Radio,
  MapPin,
  CheckCircle2,
  Send
} from 'lucide-react';
import { useAreaMotorista } from '../../contexts/AreaMotoristaContext';
import { usePermissionsContext } from '../../contexts/PermissionsContext';
import { PWAInstallBanner } from '../../components/pwa/PWAInstallBanner';
import { empresaService } from '../../services/empresaService';

export const AreaMotoristaLayout: React.FC = () => {
  const { role } = usePermissionsContext();
  const isDriver = role === 'driver';

  const {
    motoristasList,
    currentMotorista,
    setCurrentMotoristaId,
    isAgregado,
    semVinculo,
    viagemAtiva,
    isLoading,
    refreshData,
    notificacoes,
    notificacaoPendenteAlerta,
    dispensarAlerta,
    enviarLocalizacaoGps,
    transmitindoGps
  } = useAreaMotorista();

  const [selectorOpen, setSelectorOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const [gpsFeedback, setGpsFeedback] = useState<string | null>(null);
  // Telefone de suporte vem de Configurações do Sistema > Dados da Empresa
  // (campo já existente); só cai no número de exemplo se nada foi configurado.
  const [telefoneSuporte, setTelefoneSuporte] = useState<string>('0800 000 0000');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    empresaService.getEmpresaAtiva()
      .then((empresa) => {
        if (empresa?.telefone) setTelefoneSuporte(empresa.telefone);
      })
      .catch((err) => console.warn('Erro ao carregar telefone de suporte:', err));
  }, []);

  const telSuporteHref = `tel:${telefoneSuporte.replace(/[^\d+]/g, '')}`;

  // Fecha o menu mobile sempre que a rota mudar
  useEffect(() => {
    setMobileMenuOpen(false);
    setSelectorOpen(false);
    setNotifDropdownOpen(false);
  }, [location.pathname]);

  // Solicitar localização assim que o motorista abrir o painel
  useEffect(() => {
    if (currentMotorista) {
      enviarLocalizacaoGps().catch(console.error);
    }
  }, [currentMotorista, enviarLocalizacaoGps]);

  const handleTransmitirGpsManual = async () => {
    setGpsFeedback('Obtendo sinal...');
    const res = await enviarLocalizacaoGps();
    if (res.success) {
      setGpsFeedback(res.message || 'GPS transmitido!');
    } else {
      setGpsFeedback('Erro: ' + (res.message || 'Sem sinal'));
    }
    setTimeout(() => {
      setGpsFeedback(null);
    }, 3500);
  };

  const naoLidasCount = notificacoes.filter(n => !n.lida).length;

  const navItems = [
    { name: 'Início', path: '/area-motorista', icon: LayoutDashboard, end: true },
    { name: 'Roteiro', path: '/area-motorista/roteiro', icon: Navigation, highlight: true },
    { name: 'Viagens', path: '/area-motorista/viagens', icon: ClipboardList },
    { name: 'Em Rota', path: '/area-motorista/execucao', icon: Navigation2, highlight: !!viagemAtiva },
    ...(isAgregado ? [{ name: 'Extrato', path: '/area-motorista/extrato', icon: DollarSign }] : []),
    { name: 'Vistoria', path: '/area-motorista/checklist', icon: ClipboardCheck },
    { name: 'Perfil', path: '/area-motorista/perfil', icon: User },
  ];

  // Identifica se a rota atual é uma das páginas secundárias (Vistoria, Extrato, Perfil)
  const isOtherActive = ['/area-motorista/extrato', '/area-motorista/checklist', '/area-motorista/perfil'].includes(location.pathname);

  // IMPORTANTE: um usuário com papel "driver" cujo e-mail não bate com nenhum
  // motorista cadastrado NÃO pode navegar pelo app — antes disso o contexto caía
  // silenciosamente no primeiro motorista da lista, expondo ganhos/repasses/extrato
  // de outra pessoa. Papéis de staff (admin/manager) continuam podendo escolher
  // manualmente um motorista pelo seletor, para fins de suporte.
  if (!isLoading && semVinculo && isDriver) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center p-4">
        <div className="bg-slate-950 border border-slate-800 rounded-2xl p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-white mb-1">Sua conta não está vinculada a nenhum motorista</h2>
          <p className="text-xs text-slate-400 mb-6">
            Não encontramos nenhum cadastro de motorista associado ao e-mail da sua conta. Entre em contato com a
            central operacional da Voope Transportes para vincular seu acesso corretamente.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col antialiased selection:bg-red-500 selection:text-white">
      {/* Top Header */}
      <header className="sticky top-0 z-40 bg-slate-950/95 backdrop-blur-md border-b border-slate-800/80 px-3 sm:px-4 py-2.5 sm:py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-2 sm:gap-3">
          
          {/* Left: Mobile Menu Toggle / Back Button & Logo */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Mobile Hamburger Toggle */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-xl bg-slate-800/80 text-slate-300 hover:text-white hover:bg-slate-700 transition cursor-pointer"
              title={mobileMenuOpen ? 'Fechar Menu' : 'Abrir Menu do Motorista'}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5 text-red-400" /> : <Menu className="w-5 h-5" />}
            </button>

            <button
              onClick={() => navigate('/')}
              className="hidden sm:flex p-1.5 rounded-lg bg-slate-800/80 text-slate-400 hover:text-white hover:bg-slate-700 transition cursor-pointer"
              title="Voltar ao Sistema Principal"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/area-motorista')}>
              <div className="w-8 h-8 rounded-xl bg-red-600 flex items-center justify-center text-white shadow-lg shadow-red-600/30 shrink-0">
                <Truck className="w-4 h-4 stroke-[2.5]" />
              </div>
              <div className="leading-tight">
                <div className="flex items-center gap-1.5">
                  <span className="font-black text-sm tracking-tight text-white uppercase">Voope</span>
                  <span className="text-[10px] bg-red-600/20 text-red-400 font-bold px-1.5 py-0.2 rounded border border-red-500/30 uppercase tracking-wide">
                    Motorista
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 hidden sm:block">Portal Operacional</p>
              </div>
            </div>
          </div>

          {/* Center: Driver Selector for testing / Admin toggle */}
          <div className="relative">
            <button
              type="button"
              onClick={() => !isDriver && setSelectorOpen(!selectorOpen)}
              className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded-xl border border-slate-700 text-xs font-semibold text-slate-200 transition ${isDriver ? 'bg-slate-800/40 cursor-default' : 'bg-slate-800/80 hover:bg-slate-800 cursor-pointer'}`}
            >
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
              <span className="max-w-[100px] xs:max-w-[140px] sm:max-w-[200px] truncate text-[11px] sm:text-xs">
                {currentMotorista?.nome?.split(' ')[0] || 'Selecionar Motorista'}
              </span>
              {currentMotorista?.placa && (
                <span className="hidden lg:inline bg-slate-900 px-1.5 py-0.5 rounded text-[10px] text-slate-400 border border-slate-800 font-mono">
                  {currentMotorista.placa}
                </span>
              )}
              {!isDriver && <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
            </button>

            {!isDriver && selectorOpen && (
              <div className="absolute right-0 mt-2 w-72 max-w-[90vw] bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl z-50 p-2 max-h-80 overflow-y-auto animate-in fade-in zoom-in-95">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1">
                  Alternar Motorista / Agregado
                </p>
                <div className="space-y-1 mt-1">
                  {motoristasList.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => {
                        setCurrentMotoristaId(m.id);
                        setSelectorOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-xl text-xs flex flex-col gap-0.5 transition cursor-pointer ${
                        currentMotorista?.id === m.id
                          ? 'bg-red-600 text-white font-bold'
                          : 'text-slate-300 hover:bg-slate-800'
                      }`}
                    >
                      <span className="truncate">{m.nome}</span>
                      <div className="flex items-center gap-2 text-[10px] opacity-80">
                        <span>{m.tipo_vinculo || 'Motorista'}</span>
                        {m.placa && <span>• Placa: {m.placa}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Actions: GPS Broadcast, Notifications & Refresh */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Botão de Transmissão de GPS */}
            <button
              onClick={handleTransmitirGpsManual}
              disabled={transmitindoGps}
              className={`flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-bold transition shadow-sm cursor-pointer ${
                transmitindoGps
                  ? 'bg-emerald-600 text-white animate-pulse'
                  : gpsFeedback
                  ? 'bg-emerald-950 text-emerald-300 border border-emerald-500/50'
                  : 'bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-slate-700'
              }`}
              title="Transmitir GPS Atual para a Central"
            >
              <Radio className={`w-3.5 h-3.5 ${transmitindoGps ? 'animate-spin text-white' : 'text-emerald-400'}`} />
              <span className="hidden md:inline">
                {transmitindoGps ? 'Enviando...' : gpsFeedback ? 'GPS Enviado!' : 'Sinal GPS'}
              </span>
            </button>

            {/* Sino de Notificações com Dropdown */}
            <div className="relative">
              <button
                onClick={() => setNotifDropdownOpen(!notifDropdownOpen)}
                className="relative p-2 rounded-xl bg-slate-800/80 text-slate-300 hover:text-white hover:bg-slate-700 transition cursor-pointer"
                title="Mensagens da Central Operacional"
              >
                <Bell className="w-4 h-4" />
                {naoLidasCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-bounce">
                    {naoLidasCount}
                  </span>
                )}
              </button>

              {notifDropdownOpen && (
                <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl z-50 p-3 max-h-96 overflow-y-auto animate-in fade-in zoom-in-95">
                  <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-800">
                    <span className="text-xs font-bold text-white flex items-center gap-1.5">
                      <Bell className="w-3.5 h-3.5 text-red-500" />
                      Mensagens da Central
                    </span>
                    <button
                      onClick={() => setNotifDropdownOpen(false)}
                      className="text-slate-400 hover:text-white text-xs cursor-pointer p-1"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {notificacoes.length === 0 ? (
                    <p className="text-slate-500 text-xs text-center py-4">Nenhuma mensagem recente.</p>
                  ) : (
                    <div className="space-y-2">
                      {notificacoes.map((item) => (
                        <div
                          key={item.id}
                          className={`p-2.5 rounded-xl border text-xs space-y-1 transition ${
                            item.lida
                              ? 'bg-slate-950/40 border-slate-800 text-slate-400'
                              : 'bg-slate-800/80 border-red-500/30 text-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className="font-bold text-white truncate">{item.titulo}</span>
                            {!item.lida && (
                              <button
                                onClick={() => dispensarAlerta(item.id)}
                                className="text-[10px] text-red-400 hover:underline shrink-0 cursor-pointer"
                              >
                                Marcar lida
                              </button>
                            )}
                          </div>
                          <p className="text-[11px] leading-snug">{item.mensagem}</p>
                          <span className="text-[9px] text-slate-500 block pt-0.5">
                            {new Date(item.created_at).toLocaleString('pt-BR')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => refreshData()}
              disabled={isLoading}
              className="p-2 rounded-xl bg-slate-800/80 text-slate-300 hover:text-white hover:bg-slate-700 transition cursor-pointer"
              title="Atualizar Dados"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin text-red-500' : ''}`} />
            </button>

            <a
              href={telSuporteHref}
              title={`Ligar para ${telefoneSuporte}`}
              className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/30 text-xs font-bold transition"
            >
              <PhoneCall className="w-3.5 h-3.5" />
              <span>Suporte 24h</span>
            </a>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Drawer / Menu Recolhível */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop Escuro (clique para recolher o menu) */}
          <div
            className="fixed inset-0 bg-black/75 backdrop-blur-xs transition-opacity animate-in fade-in"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Drawer Lateral Deslizante */}
          <div className="relative w-4/5 max-w-xs bg-slate-950 border-r border-slate-800 flex flex-col h-full shadow-2xl z-10 animate-in slide-in-from-left duration-200">
            
            {/* Drawer Header */}
            <div className="p-4 border-b border-slate-800/80 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-red-600 flex items-center justify-center text-white shadow-md">
                  <Truck className="w-4 h-4 stroke-[2.5]" />
                </div>
                <div>
                  <h3 className="font-black text-xs uppercase tracking-wider text-white">Menu do Motorista</h3>
                  <p className="text-[10px] text-slate-400">Voope Transportes</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-1.5 rounded-xl bg-slate-900 text-slate-400 hover:text-white border border-slate-800 cursor-pointer"
                title="Fechar Menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Motorista Ativo Card */}
            {currentMotorista && (
              <div className="p-3.5 m-3 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Motorista Conectado</span>
                  <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-950 text-emerald-400 border border-emerald-800 font-bold">
                    Online
                  </span>
                </div>
                <p className="font-bold text-xs text-white truncate">{currentMotorista.nome}</p>
                <div className="flex items-center justify-between text-[11px] text-slate-400 font-mono">
                  <span>Placa: <strong className="text-slate-200">{currentMotorista.placa || 'N/D'}</strong></span>
                  <span>{currentMotorista.tipo_vinculo || (isAgregado ? 'Agregado' : 'CLT')}</span>
                </div>
              </div>
            )}

            {/* Links de Navegação */}
            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
              <span className="px-3 py-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                Navegação Principal
              </span>
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.end}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3.5 py-3 rounded-2xl text-xs font-bold transition ${
                        isActive
                          ? 'bg-red-600 text-white shadow-lg shadow-red-600/20'
                          : 'text-slate-300 hover:text-white hover:bg-slate-900'
                      } ${item.highlight && !isActive ? 'border border-red-500/40 text-red-400' : ''}`
                    }
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span>{item.name}</span>
                    {item.highlight && (
                      <span className="w-2 h-2 rounded-full bg-red-400 ml-auto animate-pulse" />
                    )}
                  </NavLink>
                );
              })}
            </div>

            {/* Footer do Drawer: Suporte 24h & Voltar ao Sistema */}
            <div className="p-3 border-t border-slate-800/80 space-y-2 bg-slate-950">
              <a
                href={telSuporteHref}
                title={`Ligar para ${telefoneSuporte}`}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/30 text-xs font-bold transition"
              >
                <PhoneCall className="w-4 h-4" />
                <span>Central de Suporte 24h</span>
              </a>

              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate('/');
                }}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white border border-slate-800 text-xs font-bold transition cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Voltar ao Sistema Central</span>
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Banner de PWA e Notificações Push */}
      <PWAInstallBanner />

      {/* Banner de Notificação Pendente / Solicitação de Localização */}
      {notificacaoPendenteAlerta && (
        <div className={`border-b px-4 py-3 shadow-lg animate-in slide-in-from-top-2 ${
          notificacaoPendenteAlerta.tipo === 'solicitacao_localizacao'
            ? 'bg-gradient-to-r from-red-950 via-red-900 to-slate-950 border-red-500/50 text-white'
            : notificacaoPendenteAlerta.tipo === 'alerta'
            ? 'bg-gradient-to-r from-amber-950 via-amber-900 to-slate-950 border-amber-500/50 text-white'
            : 'bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-slate-700 text-white'
        }`}>
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-xl bg-white/10 text-white shrink-0 mt-0.5">
                {notificacaoPendenteAlerta.tipo === 'solicitacao_localizacao' ? (
                  <MapPin className="w-5 h-5 text-red-400 animate-bounce" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                )}
              </div>
              <div>
                <h4 className="font-black text-sm flex items-center gap-2">
                  {notificacaoPendenteAlerta.titulo}
                  <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded font-mono">Central Operacional</span>
                </h4>
                <p className="opacity-90 mt-0.5 text-xs max-w-2xl leading-relaxed">
                  {notificacaoPendenteAlerta.mensagem}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
              {notificacaoPendenteAlerta.tipo === 'solicitacao_localizacao' && (
                <button
                  onClick={handleTransmitirGpsManual}
                  disabled={transmitindoGps}
                  className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl shadow-lg flex items-center gap-1.5 transition text-xs"
                >
                  <Radio className={`w-3.5 h-3.5 ${transmitindoGps ? 'animate-spin' : ''}`} />
                  <span>{transmitindoGps ? 'Transmitindo...' : 'Transmitir Sinal Agora'}</span>
                </button>
              )}
              <button
                onClick={() => dispensarAlerta(notificacaoPendenteAlerta.id)}
                className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-slate-200 hover:text-white font-semibold rounded-xl border border-white/10 transition text-xs"
              >
                Confirmar & Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live Trip Banner (if a trip is active and not on execution page) */}
      {viagemAtiva && location.pathname !== '/area-motorista/execucao' && (
        <div className="bg-gradient-to-r from-red-700 to-red-900 border-b border-red-500/40 text-white px-4 py-2.5 shadow-lg">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-2.5 h-2.5 rounded-full bg-white animate-ping shrink-0" />
              <div className="truncate">
                <span className="font-black uppercase tracking-wider mr-2">Viagem em Andamento:</span>
                <span className="font-semibold opacity-90">OS #{viagemAtiva.id} • {viagemAtiva.entrega || 'Destino em rota'}</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/area-motorista/execucao')}
              className="px-3 py-1 bg-white text-red-900 hover:bg-red-50 font-bold rounded-lg shrink-0 flex items-center gap-1 shadow transition"
            >
              <Navigation2 className="w-3.5 h-3.5" />
              <span>Abrir Rota</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Container with Desktop Sidebar / Mobile Layout */}
      <div className="flex-1 max-w-6xl w-full mx-auto flex flex-col md:flex-row pb-20 md:pb-8">
        
        {/* Desktop Sidebar Navigation */}
        <aside className="hidden md:flex flex-col w-56 p-4 border-r border-slate-800 shrink-0">
          <div className="sticky top-20 space-y-1">
            <div className="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Menu do Motorista
            </div>
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition ${
                      isActive
                        ? 'bg-red-600 text-white shadow-lg shadow-red-600/20'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                    } ${item.highlight && !isActive ? 'border border-red-500/40 text-red-400' : ''}`
                  }
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{item.name}</span>
                  {item.highlight && (
                    <span className="w-2 h-2 rounded-full bg-red-400 ml-auto animate-pulse" />
                  )}
                </NavLink>
              );
            })}

            {/* Quick Driver Card */}
            {currentMotorista && (
              <div className="mt-8 p-3 rounded-2xl bg-slate-950/60 border border-slate-800/80 text-[11px] space-y-2">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Veículo Atual</span>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 font-mono font-bold text-xs">{currentMotorista.placa || 'Sem Placa'}</span>
                  <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-950 text-emerald-400 border border-emerald-800">
                    Ativo
                  </span>
                </div>
                <p className="text-[10px] text-slate-500 truncate">{currentMotorista.nome}</p>
              </div>
            )}
          </div>
        </aside>

        {/* Content Outlet */}
        <main className="flex-1 p-3 sm:p-6 pb-24 md:pb-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* Bottom Navigation for Mobile (Smartphone First for drivers on the road) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-950/95 backdrop-blur-lg border-t border-slate-800 px-2 py-1.5 safe-area-pb">
        <div className="grid grid-cols-5 gap-1 items-center">
          {/* 1. Início */}
          <NavLink
            to="/area-motorista"
            end
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-1.5 rounded-xl text-[10px] font-bold transition min-h-[48px] ${
                isActive
                  ? 'text-red-500 bg-red-950/40'
                  : 'text-slate-400 hover:text-slate-200'
              }`
            }
          >
            <LayoutDashboard className="w-5 h-5 mb-0.5" />
            <span className="leading-tight text-[10px]">Início</span>
          </NavLink>

          {/* 2. Roteiro */}
          <NavLink
            to="/area-motorista/roteiro"
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-1.5 rounded-xl text-[10px] font-bold transition min-h-[48px] ${
                isActive
                  ? 'text-red-500 bg-red-950/40'
                  : 'text-slate-400 hover:text-slate-200'
              }`
            }
          >
            <div className="relative">
              <Navigation className="w-5 h-5 mb-0.5" />
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-400 animate-pulse" />
            </div>
            <span className="leading-tight text-[10px]">Roteiro</span>
          </NavLink>

          {/* 3. Em Rota (Destaque Central) */}
          <NavLink
            to="/area-motorista/execucao"
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-1.5 rounded-xl text-[10px] font-bold transition min-h-[48px] ${
                isActive
                  ? 'text-white bg-red-600 shadow-lg shadow-red-600/40'
                  : viagemAtiva
                  ? 'text-amber-400 bg-amber-950/30 border border-amber-500/30'
                  : 'text-slate-400 hover:text-slate-200'
              }`
            }
          >
            <div className="relative">
              <Navigation2 className="w-5 h-5 mb-0.5" />
              {viagemAtiva && (
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
              )}
            </div>
            <span className="leading-tight text-[10px]">Em Rota</span>
          </NavLink>

          {/* 4. Viagens */}
          <NavLink
            to="/area-motorista/viagens"
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-1.5 rounded-xl text-[10px] font-bold transition min-h-[48px] ${
                isActive
                  ? 'text-red-500 bg-red-950/40'
                  : 'text-slate-400 hover:text-slate-200'
              }`
            }
          >
            <ClipboardList className="w-5 h-5 mb-0.5" />
            <span className="leading-tight text-[10px]">Viagens</span>
          </NavLink>

          {/* 5. Menu / Mais */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`flex flex-col items-center justify-center py-1.5 rounded-xl text-[10px] font-bold transition min-h-[48px] cursor-pointer ${
              mobileMenuOpen || isOtherActive
                ? 'text-red-400 bg-red-950/40'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Menu className="w-5 h-5 mb-0.5" />
            <span className="leading-tight text-[10px]">Menu</span>
          </button>
        </div>
      </nav>
    </div>
  );
};
